const DEFAULT_FALLBACK = '';

type DateInput = Date | string | number | null | undefined;
type LocaleValue = string | undefined;

const isDate = (value: unknown): value is Date => value instanceof Date;

const coerceNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === 'bigint') {
    return Number(value);
  }

  return null;
};

const coerceDate = (value: DateInput): Date | null => {
  if (!value && value !== 0) {
    return null;
  }

  if (isDate(value)) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const withFallback = (value: string | null | undefined, fallback?: string): string => {
  if (value === undefined || value === null) {
    return fallback ?? DEFAULT_FALLBACK;
  }

  return value;
};

const safeToLocaleString = (
  number: number,
  locale?: string,
  formatOptions?: Intl.NumberFormatOptions
): string | null => {
  try {
    if (formatOptions) {
      return number.toLocaleString(locale, formatOptions);
    }
    return number.toLocaleString(locale);
  } catch {
    return null;
  }
};

const safeDateToLocaleString = (
  date: Date,
  locale?: string,
  formatOptions?: Intl.DateTimeFormatOptions
): string | null => {
  try {
    if (formatOptions && hasOwnKeys(formatOptions)) {
      return date.toLocaleString(locale, formatOptions);
    }
    return date.toLocaleString(locale);
  } catch {
    return null;
  }
};

const safeDateToLocaleDateString = (
  date: Date,
  locale?: string,
  formatOptions?: Intl.DateTimeFormatOptions
): string | null => {
  try {
    if (formatOptions && hasOwnKeys(formatOptions)) {
      return date.toLocaleDateString(locale, formatOptions);
    }
    return date.toLocaleDateString(locale);
  } catch {
    return null;
  }
};

const safeDateToLocaleTimeString = (
  date: Date,
  locale?: string,
  formatOptions?: Intl.DateTimeFormatOptions
): string | null => {
  try {
    if (formatOptions && hasOwnKeys(formatOptions)) {
      return date.toLocaleTimeString(locale, formatOptions);
    }
    return date.toLocaleTimeString(locale);
  } catch {
    return null;
  }
};

const hasOwnKeys = (object: unknown): object is Record<string, unknown> => {
  if (!object || typeof object !== 'object') return false;
  return Object.keys(object).length > 0;
};

const prepareDateFormattingOptions = (options?: {
  locale?: LocaleValue;
  formatOptions?: Intl.DateTimeFormatOptions;
  timeZone?: string;
  fallback?: string;
}) => {
  const locale = normalizeLocale(options?.locale);
  const formatOptions = { ...options?.formatOptions };

  if (options?.timeZone && !formatOptions.timeZone) {
    formatOptions.timeZone = options.timeZone;
  }

  return { locale, formatOptions };
};

const tryDateFormatting = (
  date: Date,
  locale: LocaleValue,
  formatOptions: Intl.DateTimeFormatOptions,
  formatter: (date: Date, locale?: string, options?: Intl.DateTimeFormatOptions) => string | null
): string | null => {
  // Try with locale first
  let result = formatter(date, locale, formatOptions);
  if (result) return result;

  // Fallback without locale
  result = formatter(date, undefined, formatOptions);
  return result;
};

/**
 * Normalize text by trimming whitespace
 * @param value - The value to normalize
 * @returns Trimmed string or empty string if not a string
 */
export const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Normalize a value to a URL-friendly key
 * @param value - The value to normalize
 * @param fallback - Fallback value if normalization results in empty string
 * @returns Normalized key string
 */
export const normalizeKey = (value: unknown, fallback = 'other'): string => {
  const text = normalizeText(value);
  if (!text) return fallback;
  return (
    text
      .toLowerCase()
      .replaceAll(/[^\da-z]+/g, '-')
      .replaceAll(/^-+|-+$/g, '') || fallback
  );
};

export const normalizeLocale = (locale?: LocaleValue, fallback?: LocaleValue): LocaleValue => {
  if (!locale || typeof locale !== 'string') {
    return fallback;
  }

  const trimmed = locale.trim();
  if (!trimmed) {
    return fallback;
  }

  const segments = trimmed.replaceAll('_', '-').split('-').filter(Boolean);
  if (segments.length === 0) {
    return fallback;
  }

  const normalized = segments
    .map((segment, index) => {
      if (index === 0) return segment.toLowerCase();
      if (index === 1 && segment.length <= 3) return segment.toUpperCase();
      return segment;
    })
    .join('-');

  return normalized || fallback;
};

export const formatNumber = (
  value: unknown,
  options?: {
    locale?: LocaleValue;
    formatOptions?: Intl.NumberFormatOptions;
    fallback?: string;
  }
): string => {
  const number = coerceNumber(value);
  if (number === null) {
    return withFallback(undefined, options?.fallback);
  }

  const locale = normalizeLocale(options?.locale);

  // Try with locale first
  let result = safeToLocaleString(number, locale, options?.formatOptions);
  if (result) return result;

  // Fallback without locale
  result = safeToLocaleString(number, undefined, options?.formatOptions);
  if (result) return result;

  // Final fallback
  return withFallback(String(number), options?.fallback);
};

export const formatCurrency = (
  value: unknown,
  options?: {
    locale?: LocaleValue;
    currency?: string;
    formatOptions?: Intl.NumberFormatOptions;
    fallback?: string;
  }
): string => {
  const number = coerceNumber(value);
  if (number === null) {
    return withFallback(undefined, options?.fallback);
  }

  const formatNumberOptions: {
    locale?: LocaleValue;
    formatOptions?: Intl.NumberFormatOptions;
    fallback?: string;
  } = {
    formatOptions: {
      style: 'currency',
      currency: options?.currency ?? 'USD',
      ...options?.formatOptions,
    },
  };

  if (options?.locale) {
    formatNumberOptions.locale = normalizeLocale(options.locale);
  }

  if (options?.fallback !== undefined) {
    formatNumberOptions.fallback = options.fallback;
  }

  return formatNumber(number, formatNumberOptions);
};

export const formatDateTime = (
  value: DateInput,
  options?: {
    locale?: LocaleValue;
    formatOptions?: Intl.DateTimeFormatOptions;
    timeZone?: string;
    fallback?: string;
  }
): string => {
  const date = coerceDate(value);
  if (!date) {
    return withFallback(undefined, options?.fallback);
  }

  const { locale, formatOptions } = prepareDateFormattingOptions(options);
  const result = tryDateFormatting(date, locale, formatOptions, safeDateToLocaleString);

  return result ?? withFallback(date.toISOString(), options?.fallback);
};

export const formatDate = (
  value: DateInput,
  options?: {
    locale?: LocaleValue;
    formatOptions?: Intl.DateTimeFormatOptions;
    timeZone?: string;
    fallback?: string;
  }
): string => {
  const date = coerceDate(value);
  if (!date) {
    return withFallback(undefined, options?.fallback);
  }

  const { locale, formatOptions } = prepareDateFormattingOptions(options);
  const result = tryDateFormatting(date, locale, formatOptions, safeDateToLocaleDateString);

  return result ?? withFallback('', options?.fallback);
};

export const formatTime = (
  value: DateInput,
  options?: {
    locale?: LocaleValue;
    formatOptions?: Intl.DateTimeFormatOptions;
    timeZone?: string;
    fallback?: string;
  }
): string => {
  const date = coerceDate(value);
  if (!date) {
    return withFallback(undefined, options?.fallback);
  }

  const { locale, formatOptions } = prepareDateFormattingOptions(options);
  const result = tryDateFormatting(date, locale, formatOptions, safeDateToLocaleTimeString);

  return result ?? withFallback('', options?.fallback);
};

/**
 * Normalize Unicode characters (especially Vietnamese) to ASCII equivalents
 * This is crucial for Vietnamese text which contains special diacritical marks
 * that standard normalization doesn't handle properly
 */
const normalizeUnicode = (str: string): string => {
  if (!str) return '';

  // Vietnamese character mapping
  const vietnameseMap: Record<string, string> = {
    à: 'a',
    á: 'a',
    ạ: 'a',
    ả: 'a',
    ã: 'a',
    â: 'a',
    ầ: 'a',
    ấ: 'a',
    ậ: 'a',
    ẩ: 'a',
    ẫ: 'a',
    ă: 'a',
    ằ: 'a',
    ắ: 'a',
    ặ: 'a',
    ẳ: 'a',
    ẵ: 'a',
    è: 'e',
    é: 'e',
    ẹ: 'e',
    ẻ: 'e',
    ẽ: 'e',
    ê: 'e',
    ề: 'e',
    ế: 'e',
    ệ: 'e',
    ể: 'e',
    ễ: 'e',
    ì: 'i',
    í: 'i',
    ĩ: 'i',
    ị: 'i',
    ỉ: 'i',
    ò: 'o',
    ó: 'o',
    ọ: 'o',
    ỏ: 'o',
    õ: 'o',
    ô: 'o',
    ồ: 'o',
    ố: 'o',
    ộ: 'o',
    ổ: 'o',
    ỗ: 'o',
    ơ: 'o',
    ờ: 'o',
    ớ: 'o',
    ợ: 'o',
    ở: 'o',
    ỡ: 'o',
    ù: 'u',
    ú: 'u',
    ụ: 'u',
    ủ: 'u',
    ũ: 'u',
    ư: 'u',
    ừ: 'u',
    ứ: 'u',
    ự: 'u',
    ử: 'u',
    ữ: 'u',
    ỳ: 'y',
    ý: 'y',
    ỵ: 'y',
    ỷ: 'y',
    ỹ: 'y',
    đ: 'd',
    // Uppercase variants
    À: 'A',
    Á: 'A',
    Ạ: 'A',
    Ả: 'A',
    Ã: 'A',
    Â: 'A',
    Ầ: 'A',
    Ấ: 'A',
    Ậ: 'A',
    Ẩ: 'A',
    Ẫ: 'A',
    Ă: 'A',
    Ằ: 'A',
    Ắ: 'A',
    Ặ: 'A',
    Ẳ: 'A',
    Ẵ: 'A',
    È: 'E',
    É: 'E',
    Ẹ: 'E',
    Ẻ: 'E',
    Ẽ: 'E',
    Ê: 'E',
    Ề: 'E',
    Ế: 'E',
    Ệ: 'E',
    Ể: 'E',
    Ễ: 'E',
    Ì: 'I',
    Í: 'I',
    Ĩ: 'I',
    Ị: 'I',
    Ỉ: 'I',
    Ò: 'O',
    Ó: 'O',
    Ọ: 'O',
    Ỏ: 'O',
    Õ: 'O',
    Ô: 'O',
    Ồ: 'O',
    Ố: 'O',
    Ộ: 'O',
    Ổ: 'O',
    Ỗ: 'O',
    Ơ: 'O',
    Ờ: 'O',
    Ớ: 'O',
    Ợ: 'O',
    Ở: 'O',
    Ỡ: 'O',
    Ù: 'U',
    Ú: 'U',
    Ụ: 'U',
    Ủ: 'U',
    Ũ: 'U',
    Ư: 'U',
    Ừ: 'U',
    Ứ: 'U',
    Ự: 'U',
    Ử: 'U',
    Ữ: 'U',
    Ỳ: 'Y',
    Ý: 'Y',
    Ỵ: 'Y',
    Ỷ: 'Y',
    Ỹ: 'Y',
    Đ: 'D',
  };

  // Replace Vietnamese characters
  let normalized = [...str].map(char => vietnameseMap[char] ?? char).join('');

  // Use NFD normalization to separate base characters from diacritics, then remove diacritics
  // This handles any remaining Unicode characters from other languages
  normalized = normalized.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');

  return normalized;
};

export const formatSlug = (
  value: unknown,
  options?: {
    separator?: string;
    fallback?: string;
  }
): string => {
  if (value === null || value === undefined) {
    return withFallback(undefined, options?.fallback);
  }

  const separator = options?.separator ?? '-';

  // First normalize Vietnamese and other Unicode characters to ASCII
  const normalized = normalizeUnicode(
    typeof value === 'string'
      ? value
      : typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : ''
  );

  // Then apply standard slug processing
  const processed = normalized
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replaceAll(/[^\da-z]+/g, separator)
    .replaceAll(new RegExp(`${separator}{2,}`, 'g'), separator)
    .replaceAll(new RegExp(`^${separator}|${separator}$`, 'g'), '');

  return withFallback(processed, options?.fallback);
};

export const ensureLocale = (
  preferredLocale?: LocaleValue,
  fallback?: LocaleValue
): LocaleValue => {
  return normalizeLocale(preferredLocale, normalizeLocale(fallback, 'en'));
};

/**
 * Format category or facility type strings for display
 * Converts kebab-case or snake_case to Title Case with proper spacing
 * @param value - The category/facility type string to format
 * @param options - Formatting options
 * @returns Formatted display string
 */
export const formatCategory = (
  value: unknown,
  options?: {
    separator?: string;
    fallback?: string;
  }
): string => {
  if (!value || typeof value !== 'string') {
    return withFallback(undefined, options?.fallback);
  }

  const separator = options?.separator ?? ' ';

  // Replace dashes and underscores with spaces, then capitalize each word
  return value.replaceAll(/[_-]/g, separator).replaceAll(/\b\w/g, char => char.toUpperCase());
};
